import React, { useEffect, useRef } from 'react';
import ChoicePicker from '../../components/ChoicePicker';
import TextInput from '../../components/TextInput';
import WheelDatePicker from '../../components/WheelDatePicker';
import { sendIcon } from '../../chatIcons';
import type { OtherIncomeEarnerFlow } from './useOtherIncomeEarnerFlow';
import type { EarnerRole, MemberToEarnerRelationship, OtherIncomeChangeReason } from './otherIncomeEarnerTypes';
import {
  EARNER_ROLE_OPTIONS,
  MEMBER_TO_EARNER_RELATIONSHIP_OPTIONS,
  OTHER_INCOME_CHANGE_REASON_OPTIONS,
} from './otherIncomeEarnerTypes';

const AUTO_INTRO_MS = 550;

const YES_NO = (t: OtherIncomeEarnerFlow['t']) => [
  { value: 'yes' as const, label: t('submitDocuments.dependentDeduction.otherIncomeEarner.yes') },
  { value: 'no' as const, label: t('submitDocuments.dependentDeduction.otherIncomeEarner.no') },
];

export default function OtherIncomeEarnerChatComposer({
  flow,
  wheelLocale,
}: {
  flow: OtherIncomeEarnerFlow;
  wheelLocale: string;
}) {
  const tokenRef = useRef(0);

  useEffect(() => {
    if (flow.isLoading || flow.reviewMode) return;
    if (flow.step !== 'intro') return;
    const token = ++tokenRef.current;
    const id = window.setTimeout(() => {
      if (tokenRef.current !== token) return;
      void flow.skipIntro();
    }, AUTO_INTRO_MS);
    return () => window.clearTimeout(id);
  }, [flow]);

  if (flow.reviewMode && flow.step === 'review') return null;

  if (flow.step === 'intro') return null;

  if (flow.step === 'anyClaimedGate') {
    return (
      <div style={{ padding: '12px 16px' }}>
        <ChoicePicker value={null} options={YES_NO(flow.t)} onChange={(v) => void flow.handleAnyClaimed(v)} />
      </div>
    );
  }

  if (flow.step === 'pickMember') {
    const opts = flow.availableCandidates().map((c) => ({
      value: String(c.empFamilyId),
      label: `${c.fullName} (${flow.t(`submitDocuments.familyInfo.relationship.${c.relationship}`)})`,
    }));
    return (
      <div style={{ padding: '12px 16px' }}>
        <ChoicePicker
          value={null}
          options={opts}
          onChange={(v) => {
            const c = flow.candidates.find((x) => String(x.empFamilyId) === v);
            if (c) void flow.handlePickMember(c);
          }}
        />
      </div>
    );
  }

  if (flow.step === 'earnerRole') {
    const opts = EARNER_ROLE_OPTIONS.map((v) => ({
      value: v,
      label: flow.t(`submitDocuments.dependentDeduction.otherIncomeEarner.earnerRole.${v}`),
    }));
    return (
      <div style={{ padding: '12px 16px' }}>
        <ChoicePicker value={null} options={opts} onChange={(v) => void flow.handleEarnerRole(v as EarnerRole)} />
      </div>
    );
  }

  if (flow.step === 'earnerRoleOther') {
    return (
      <TextInput
        fields={[
          {
            id: 'roleOther',
            label: flow.t('submitDocuments.dependentDeduction.otherIncomeEarner.earnerRoleOtherLabel'),
            required: true,
          },
        ]}
        values={{ roleOther: flow.draft.earnerRoleOther }}
        onChange={(_id, v) => flow.setDraft((d) => ({ ...d, earnerRoleOther: v }))}
        onSubmit={() => void flow.handleEarnerRoleOther(flow.draft.earnerRoleOther)}
        submitDisabled={!flow.draft.earnerRoleOther.trim()}
        submitAriaLabel={flow.t('submitDocuments.profileName.send')}
        requiredMessage={flow.t('submitDocuments.profileName.validation.required')}
      />
    );
  }

  if (flow.step === 'earnerName') {
    return (
      <TextInput
        fields={[
          {
            id: 'name',
            label: flow.t('submitDocuments.dependentDeduction.otherIncomeEarner.earnerNameLabel'),
            required: true,
          },
        ]}
        values={{ name: flow.draft.earnerName }}
        onChange={(_id, v) => flow.setDraft((d) => ({ ...d, earnerName: v }))}
        onSubmit={() => void flow.handleEarnerName(flow.draft.earnerName)}
        submitDisabled={!flow.draft.earnerName.trim()}
        submitAriaLabel={flow.t('submitDocuments.profileName.send')}
        requiredMessage={flow.t('submitDocuments.profileName.validation.required')}
      />
    );
  }

  if (flow.step === 'earnerAddress') {
    return (
      <TextInput
        fields={[
          {
            id: 'address',
            label: flow.t('submitDocuments.dependentDeduction.otherIncomeEarner.earnerAddressLabel'),
            required: true,
          },
        ]}
        values={{ address: flow.draft.earnerAddress }}
        onChange={(_id, v) => flow.setDraft((d) => ({ ...d, earnerAddress: v }))}
        onSubmit={() => void flow.handleEarnerAddress(flow.draft.earnerAddress)}
        submitDisabled={!flow.draft.earnerAddress.trim()}
        submitAriaLabel={flow.t('submitDocuments.profileName.send')}
        requiredMessage={flow.t('submitDocuments.profileName.validation.required')}
      />
    );
  }

  if (flow.step === 'memberToEarnerRelationship') {
    const opts = MEMBER_TO_EARNER_RELATIONSHIP_OPTIONS.map((v) => ({
      value: v,
      label: flow.t(`submitDocuments.dependentDeduction.otherIncomeEarner.memberRelationship.${v}`),
    }));
    return (
      <div style={{ padding: '12px 16px' }}>
        <ChoicePicker
          value={null}
          options={opts}
          onChange={(v) => void flow.handleMemberRelationship(v as MemberToEarnerRelationship)}
        />
      </div>
    );
  }

  if (flow.step === 'changedDuringYear') {
    return (
      <div style={{ padding: '12px 16px' }}>
        <ChoicePicker value={null} options={YES_NO(flow.t)} onChange={(v) => void flow.handleChangedDuringYear(v)} />
      </div>
    );
  }

  if (flow.step === 'changeDate') {
    return (
      <div className="ff-text-input">
        <div className="ff-text-input__fields">
          <WheelDatePicker
            value={flow.changeDateWheel}
            onChange={flow.setChangeDateWheel}
            locale={wheelLocale}
          />
        </div>
        <div className="ff-text-input__footer">
          <button
            type="button"
            className="ff-text-input__send"
            onClick={() => void flow.handleSendChangeDate()}
            aria-label={flow.t('submitDocuments.profileName.send')}
          >
            <img src={sendIcon} alt="" aria-hidden />
          </button>
        </div>
      </div>
    );
  }

  if (flow.step === 'changeReason') {
    const opts = OTHER_INCOME_CHANGE_REASON_OPTIONS.map((v) => ({
      value: v,
      label: flow.t(`submitDocuments.dependentDeduction.otherIncomeEarner.changeReason.${v}`),
    }));
    return (
      <div style={{ padding: '12px 16px' }}>
        <ChoicePicker
          value={null}
          options={opts}
          onChange={(v) => void flow.handleChangeReason(v as OtherIncomeChangeReason)}
        />
      </div>
    );
  }

  if (flow.step === 'changeReasonOther') {
    return (
      <TextInput
        fields={[
          {
            id: 'reasonOther',
            label: flow.t('submitDocuments.dependentDeduction.otherIncomeEarner.changeReasonOtherLabel'),
            required: true,
          },
        ]}
        values={{ reasonOther: flow.draft.changeReasonOther }}
        onChange={(_id, v) => flow.setDraft((d) => ({ ...d, changeReasonOther: v }))}
        onSubmit={() => void flow.handleChangeReasonOther(flow.draft.changeReasonOther)}
        submitDisabled={!flow.draft.changeReasonOther.trim()}
        submitAriaLabel={flow.t('submitDocuments.profileName.send')}
        requiredMessage={flow.t('submitDocuments.profileName.validation.required')}
      />
    );
  }

  if (flow.step === 'addAnother') {
    const opts = YES_NO(flow.t).filter((o) => o.value !== 'yes' || flow.canAddAnotherMember);
    return (
      <div style={{ padding: '12px 16px' }}>
        <ChoicePicker value={null} options={opts} onChange={(v) => void flow.handleAddAnother(v)} />
      </div>
    );
  }

  return null;
}
